import React from 'react'
import InitialAvatar from 'shared/components/InitialAvatar/InitialAvatar'
import Modal from 'shared/ui/Modal/Modal'
import { SortableHeaderCell } from 'shared/components/DataTable/components/SortableHeaderCell/SortableHeaderCell'
import FiltersBar from 'pages/ApplicationsPage/components/FiltersBar/FiltersBar'
import { useIsMobileLg } from 'shared/hooks/useBreakpoint'
import { ReactComponent as TrashIcon } from 'assets/icons/trash-2.svg'
import SelectDropdown from 'shared/ui/SelectDropdown/SelectDropdown'
import s from './styles/Counselors.module.scss'

const mkSortOptions = (key, labels) => [
	{ id: `${key}_asc`, key, dir: 'asc', label: labels?.asc || 'A → Z' },
	{ id: `${key}_desc`, key, dir: 'desc', label: labels?.desc || 'Z → A' },
]

const SORT_OPTIONS = {
	counselor: mkSortOptions('counselor'),
	students: mkSortOptions('students', { asc: 'Low → High', desc: 'High → Low' }),
	access: mkSortOptions('access'),
}

const COLUMNS = [
	{ id: 'counselor', label: 'Counsellor' },
	{ id: 'students', label: 'Assigned students' },
	{ id: 'access', label: 'Access level' },
	{ id: 'actions', label: '' },
]

export const Counselors = ({ vm, isSourceEmpty = false }) => {
	const {
		filtered,
		search = '',
		sortKey,
		sortDir,
		filterChips,
		setSort,
		removeFilterChip,
		clearAllPanelFilters,
		setAccess,
		accessOptions,
		savingAccessId,
		removingId,
		deleteConfirm,
		requestRemoveCounselor,
		cancelDeleteConfirm,
		confirmDelete,
	} = vm

	const emptyMessage = search.trim()
		? 'No counsellors match this search.'
		: isSourceEmpty
			? 'No counsellors in this organization yet.'
			: 'No counsellors match this search.'

	const isMobileTable = useIsMobileLg()
	const counselorAvatarSize = isMobileTable ? 28 : 34

	return (
		<div className={s.wrap}>
			{filterChips.length > 0 ? (
				<div className={s.pills}>
					<FiltersBar
						chips={filterChips}
						onRemoveChip={removeFilterChip}
						onClearAll={clearAllPanelFilters}
					/>
				</div>
			) : null}

			<div className={s.scroll}>
				<table className={s.table}>
					<thead>
						<tr>
							{COLUMNS.map(col => (
								<th
									key={col.id}
									className={col.id === 'counselor' ? s.stickyName : undefined}
								>
									{col.id === 'actions' ? null : (
										<SortableHeaderCell
											className={s.sortHead}
											labelClassName={s.sortLabel}
											label={col.label}
											isActive={sortKey === col.id}
											dir={sortDir}
											options={SORT_OPTIONS[col.id]}
											onSelect={opt =>
												setSort(opt.key, opt.dir, `${col.label}: ${opt.label}`)
											}
										/>
									)}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{filtered.map(row => (
							<tr key={row.id}>
								<td className={s.stickyName}>
									<span className={s.person}>
										<InitialAvatar name={row.name} size={counselorAvatarSize} />
										<strong>{row.name}</strong>
									</span>
								</td>
								<td className={s.assigned}>
									{row.assignedCount}{' '}
									{row.assignedCount === 1 ? 'student' : 'students'}
								</td>
								<td>
								<SelectDropdown
										value={row.access}
										options={accessOptions}
										disabled={savingAccessId === row.id || removingId === row.id}
										onChange={value => setAccess(row.id, value)}
										fullWidth={false}
										minMenuWidth={148}
										highlightActiveBackground={false}
									/>
								</td>
								<td className={s.actions}>
									<button
										type="button"
										className={s.trash}
										onClick={() => requestRemoveCounselor(row)}
										disabled={removingId === row.id}
										aria-label={`Remove ${row.name}`}
									>
										<TrashIcon aria-hidden />
									</button>
								</td>
							</tr>
						))}
						{!filtered.length ? (
							<tr>
								<td colSpan="4" className={s.empty}>
									{emptyMessage}
								</td>
							</tr>
						) : null}
					</tbody>
				</table>
			</div>

			<Modal
				isOpen={Boolean(deleteConfirm)}
				onClose={cancelDeleteConfirm}
				title="Remove counsellor"
				subtitle={
					deleteConfirm?.assignedCount
						? `${deleteConfirm.name} still has ${deleteConfirm.assignedCount} assigned student${
								deleteConfirm.assignedCount === 1 ? '' : 's'
							}. Reassign or unlink those students before removing this counsellor.`
						: `Remove ${deleteConfirm?.name || 'this counsellor'} from this organization? Their account will not be deleted globally.`
				}
				ariaLabel="Remove counsellor confirmation"
				width={520}
				footerButtons={[
					{
						id: 'cancel',
						text: 'Cancel',
						variant: 'secondary',
						onClick: cancelDeleteConfirm,
						disabled: Boolean(removingId),
					},
					{
						id: 'remove',
						text: removingId ? 'Removing…' : 'Remove counsellor',
						variant: 'primary',
						onClick: confirmDelete,
						disabled: Boolean(removingId) || Boolean(deleteConfirm?.assignedCount),
					},
				]}
				closeOnBackdrop={!removingId}
				closeOnEsc={!removingId}
			/>
		</div>
	)
}

export default Counselors
